import { twMerge } from 'tailwind-merge'
import type { ContextForecast } from '../../../../utils/context-forecast'
import type { ChatUsage } from '../../../../store'

const WARNING_FRACTION = 0.8
const FULL_FRACTION = 0.95

// Past this many messages the estimate is noise; "plenty of room" says what matters.
const TURNS_LEFT_CAP = 500

const RING_SIZE = 16
const RING_STROKE = 2
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2

const compactTokens = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })
const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

/** The context the chat is measured against: the next message's model window. */
export interface ChatUsageContext {
  forecast: ContextForecast
  contextWindow: number
  /** The next message's model, when its window is the one measured against. */
  modelLabel?: string
}

function arcClass(usedFraction: number): string {
  if (usedFraction >= FULL_FRACTION) return 'stroke-red'
  if (usedFraction >= WARNING_FRACTION) return 'stroke-warning'
  return 'stroke-foreground-secondary'
}

export function formatCredits(credits: number): string {
  if (credits === 0) return '0'
  if (credits < 0.1) return '<0.1'
  return credits.toLocaleString('en-US', { maximumFractionDigits: credits < 10 ? 1 : 0 })
}

/** "since Sep 3" when the credits leave out spend from before tracking began, else null. */
export function creditsSince({ complete, trackedSince }: Pick<ChatUsage, 'complete' | 'trackedSince'>): string | null {
  if (complete || trackedSince === null) return null
  return `since ${shortDate.format(trackedSince)}`
}

function runwayText({ forecast, modelLabel }: ChatUsageContext): string | null {
  const { usedFraction, fits, turnsLeft } = forecast
  if (!fits) {
    return `This chat is too long for ${modelLabel ?? 'the selected model'}. Switch to a model with a larger context window, or start a new chat.`
  }
  if (usedFraction >= FULL_FRACTION) return 'Almost full. Start a new chat to keep going.'
  if (turnsLeft === null) return null
  if (turnsLeft > TURNS_LEFT_CAP) return 'Plenty of room left in this chat.'
  if (turnsLeft === 0) return 'The next message may not fit. Start a new chat to keep going.'
  return `About ${turnsLeft} more ${turnsLeft === 1 ? 'message' : 'messages'} at this pace.`
}

/** The context window's fill as a ring; amber from 80%, red from 95%. */
export function ChatUsageRing({ usedFraction }: { usedFraction: number }) {
  const circle = { cx: RING_SIZE / 2, cy: RING_SIZE / 2, r: RING_RADIUS, fill: 'none', strokeWidth: RING_STROKE }

  return (
    <svg aria-hidden="true" viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} className="size-4 -rotate-90">
      <circle {...circle} className="stroke-foreground-disabled" />
      <circle
        {...circle}
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={100}
        strokeDashoffset={100 * (1 - usedFraction)}
        className={twMerge(arcClass(usedFraction), 'transition-[stroke-dashoffset] duration-300')}
      />
    </svg>
  )
}

/**
 * Context fill and the messages left at this chat's pace, when the window is
 * known, and the credits the chat has used.
 */
export function ChatUsageDetails({ usage, context }: { usage: ChatUsage; context?: ChatUsageContext }) {
  const runway = context && runwayText(context)
  const since = creditsSince(usage)

  return (
    <div className="flex w-56 flex-col gap-2 whitespace-normal py-0.5">
      {context && (
        <div className="flex flex-col gap-0.5">
          <div className="flex justify-between gap-3">
            <span className="text-foreground-secondary">Context</span>
            <span>
              {compactTokens.format(usage.contextTokens)} / {compactTokens.format(context.contextWindow)} ·{' '}
              {Math.round(context.forecast.usedFraction * 100)}%
            </span>
          </div>
          {runway && <span className="text-foreground-secondary text-xs">{runway}</span>}
        </div>
      )}
      <div className="flex justify-between gap-3">
        <span className="text-foreground-secondary">{since ? `Credits used ${since}` : 'Credits used'}</span>
        <span>{formatCredits(usage.credits)}</span>
      </div>
    </div>
  )
}
